// Pure colorbar rendering + colormap stats — no pane deps, no viewer.
// The driver-agnostic half of what was once src/ui/colormap.ts.

import { calculateColormapBounds as colormapStatsPure, symlog, symlogInv } from '../physics/colormap';
import type { FieldMetadata, ColorRGB } from '../physics/types';
import type { ColorbarUnitsMode } from '../store/schema';

export interface ColormapStatsResult {
    minBound: number;
    maxBound: number;
    stdDev: number;
    median: number;
    absoluteMin: number;
    absoluteMax: number;
    hasBothSigns: boolean;
}

export function calculateColormapBoundsFromArray(componentData: Float32Array | null | undefined, opts: { centerOnZero?: boolean } = {}): ColormapStatsResult {
    const { centerOnZero = false } = opts;
    const data = componentData ?? new Float32Array(0);
    const s = colormapStatsPure(data);
    if (s.hasBothSigns || centerOnZero) {
        return { minBound: -s.stdDev, maxBound: s.stdDev, stdDev: s.stdDev, median: s.median, absoluteMin: s.absMin, absoluteMax: s.absMax, hasBothSigns: s.hasBothSigns };
    }
    return {
        minBound: Math.max(s.absMin, s.median - s.stdDev),
        maxBound: Math.min(s.absMax, s.median + s.stdDev),
        stdDev: s.stdDev, median: s.median, absoluteMin: s.absMin, absoluteMax: s.absMax, hasBothSigns: s.hasBothSigns,
    };
}

export function toggleColorbarCollapse(): void {
    // Visual feedback comes from the `.pane` CSS transitions — width/height
    // collapse + chevron rotation. No inline opacity flash (it would collide
    // with the cold-start opacity transition baked into .pane).
    const colorbar = document.getElementById('colorbar-container');
    if (!colorbar) return;
    colorbar.classList.toggle('collapsed');
}

export function formatStatsMedian1Std(stats: { median?: number; stdDev: number } | null | undefined): string {
    if (!stats) return '';
    const { stdDev } = stats;
    const median = (typeof stats.median !== 'undefined') ? stats.median : 0;
    const fmt = (v: number): string => (isFinite(v) ? v.toFixed(3) : 'n/a');
    return `${fmt(median)} ± ${fmt(stdDev)}`;
}

export function formatStatsRange(stats: { absoluteMin: number; absoluteMax: number } | null | undefined): string {
    if (!stats) return '';
    const { absoluteMin, absoluteMax } = stats;
    const fmt = (v: number): string => (isFinite(v) ? v.toFixed(3) : 'n/a');
    return `[${fmt(absoluteMin)}, ${fmt(absoluteMax)}]`;
}

interface ColorbarLabelCtx {
    colormapMin: number;
    colormapMax: number;
    fieldMetadata: FieldMetadata | Partial<FieldMetadata> | null;
    colormapLog?: boolean;
    /**
     * Units mode for the label prefix. 'native' / 'code' inject a `[native]`
     * / `[code]` tag in front of the unit string; undefined keeps the
     * legacy "no prefix" behavior so callers that don't care stay unchanged.
     */
    unitsMode?: ColorbarUnitsMode;
}

/** Truncate `s` to at most `max` characters; if cut, append "…". */
function ellipsize(s: string, max: number): string {
    if (s.length <= max) return s;
    return `${s.slice(0, Math.max(0, max - 1))}…`;
}

/** Derive a label from the field components when no explicit name exists.
 *  e.g. ['Bx','By','Bz'] → '|B|' (magnitude of a vector), ['rho'] → 'rho'.
 *  Strips a trailing axis suffix so 'Bx' collapses to 'B'. */
function deriveLabelFromComponents(comps: readonly string[] | undefined): string {
    if (!comps || comps.length === 0) return 'Field';
    const first = comps[0]!;
    const base = (first.length > 1 && /[xyz]$/i.test(first)) ? first.slice(0, -1) : first;
    return comps.length > 1 ? `|${base}|` : base;
}

/** Pick the label that overlays the gradient when the colorbar is collapsed.
 *  Preference order: explicit short name → truncated long name → derived
 *  from field_components (e.g. '|B|'). Truncation length is sized for
 *  the 110px-long collapsed gradient at the title font size. */
function miniLabelFor(meta: Partial<FieldMetadata> | null): string {
    if (!meta) return '';
    const short = (meta.field_name_short ?? '').trim();
    if (short) return short;
    const long = (meta.field_name_long ?? '').trim();
    if (long) return ellipsize(long, 12);
    return deriveLabelFromComponents(meta.field_components);
}

/**
 * Update the 6 numeric tick labels + the optional unit/mini labels in a
 * colorbar entry. By default mutates the elements with global IDs / class
 * names (legacy single-colorbar layout). When `scope` is supplied, the
 * labels and unit/mini elements are scoped to that root — used by the
 * Stage 2 colorbar stack to update slot 2 / overflow chips independently.
 */
export function updateColorbarLabels(ctx: ColorbarLabelCtx, scope?: HTMLElement | null): void {
    const { colormapMin, colormapMax, fieldMetadata, colormapLog, unitsMode } = ctx;
    const root: Document | HTMLElement = scope ?? document;
    const labels = root.querySelectorAll('.colorbar-label');
    const unitLabel = root.querySelector('.colorbar-unit-vertical') as HTMLElement | null;
    const miniLabel = scope
        ? (scope.querySelector('.colorbar-mini-label') as HTMLElement | null)
        : document.getElementById('colorbar-mini-label');
    if (!(labels && labels.length >= 6)) return;
    // Ticks at t = 1.0, 0.8, 0.6, 0.4, 0.2, 0.0 (top → bottom of the gradient).
    // For linear mapping these are evenly spaced in data-space; for symlog
    // we evenly space in symlog-space then invert so the labels match
    // what the shader actually renders at each fractional position.
    const tickT = [1.0, 0.8, 0.6, 0.4, 0.2, 0.0];
    const valueAt = colormapLog
        ? (t: number): number => {
            const sMin = symlog(colormapMin), sMax = symlog(colormapMax);
            return symlogInv(sMin + t * (sMax - sMin));
        }
        : (t: number): number => colormapMin + t * (colormapMax - colormapMin);
    // Picks a format that stays informative across linear (e.g. 0.42) and log
    // (e.g. 1.2e-3) ranges — toFixed loses precision for the latter.
    const fmt = (v: number): string => {
        if (!isFinite(v)) return 'n/a';
        const a = Math.abs(v);
        if (a !== 0 && (a < 0.01 || a >= 1000)) return v.toExponential(1);
        return v.toFixed(2);
    };
    for (let i = 0; i < 6; i++) {
        labels[i]!.textContent = fmt(valueAt(tickT[i]!));
    }
    if (unitLabel && fieldMetadata && (fieldMetadata as { field_units?: string }).field_units) {
        const units = (fieldMetadata as { field_units: string }).field_units;
        const prefix = unitsMode ? `[${unitsMode}] ` : '';
        const truncated = units.length > 20 ? `${units.substring(0, 18)}...` : units;
        unitLabel.textContent = `${prefix}Field Magnitude (${truncated})`;
        if (units.length > 20) { unitLabel.title = `${prefix}Field Magnitude (${units})`; unitLabel.style.cursor = 'help'; }
        else { unitLabel.title = ''; unitLabel.style.cursor = 'default'; }
    }
    if (miniLabel) {
        const text = miniLabelFor(fieldMetadata as Partial<FieldMetadata> | null);
        miniLabel.textContent = text;
        // CSS uses :empty to hide the slot when there's nothing to show.
        const longName = (fieldMetadata as Partial<FieldMetadata> | null)?.field_name_long ?? '';
        miniLabel.title = (text && text !== longName) ? longName : '';
    }
}

interface ColorbarCtx extends ColorbarLabelCtx {
    currentColormap: string;
    sampleColormap?: (name: string, t: number) => ColorRGB;
    /** Optional override target. Defaults to the legacy `#colorbar` element. */
    target?: HTMLElement | null;
    /** Optional override scope for label/unit/mini lookup. */
    scope?: HTMLElement | null;
}

// Stash the latest context so the `paneedgechange` listener can repaint
// with the orientation that matches the new dock edge.
let lastCtx: ColorbarCtx | null = null;
let edgeListenerInstalled = false;

export function updateColorbar(ctx: ColorbarCtx): void {
    lastCtx = ctx;
    const { currentColormap, sampleColormap } = ctx;
    const colorbar = ctx.target ?? document.getElementById('colorbar');
    const container = document.getElementById('colorbar-container');
    if (!colorbar) return;

    if (container && !edgeListenerInstalled) {
        container.addEventListener('paneedgechange', () => {
            if (lastCtx) updateColorbar(lastCtx);
        });
        // Re-paint when the .collapsed class toggles so the canvas matches
        // the CSS-clipped size and the full gradient stays visible.
        const mo = new MutationObserver(() => {
            if (lastCtx) updateColorbar(lastCtx);
        });
        mo.observe(container, { attributes: true, attributeFilter: ['class'] });
        edgeListenerInstalled = true;
    }

    const edge = container?.dataset.edge ?? 'right';
    const horizontal = edge === 'top' || edge === 'bottom';
    // Always render at the expanded pixel size — the inner `.colorbar` div
    // CSS-transitions its display dimensions between expanded and collapsed,
    // and the canvas (sized 100%/100% via CSS) scales with it. Rendering
    // at a single size avoids the canvas snapping when .collapsed toggles.
    const longDim = horizontal ? 380 : 200;
    const shortDim = 20;
    const W = horizontal ? longDim : shortDim;
    const H = horizontal ? shortDim : longDim;

    colorbar.style.backgroundImage = '';
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const cctx = canvas.getContext('2d');
    if (!cctx) return;
    // Gradient direction: vertical → top=max, bottom=min;
    //                    horizontal → right=max, left=min.
    const gradient = horizontal
        ? cctx.createLinearGradient(0, 0, W, 0)
        : cctx.createLinearGradient(0, 0, 0, H);
    const steps = 64;
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const c: ColorRGB = (typeof sampleColormap === 'function')
            ? sampleColormap(currentColormap, t)
            : { r: Math.round(t * 255), g: Math.round(t * 255), b: Math.round(t * 255) };
        // For vertical: top is max (t=1) so stop = 1 - t to invert.
        // For horizontal: right is max (t=1) so stop = t (no inversion).
        const stop = horizontal ? t : (1 - t);
        gradient.addColorStop(stop, `rgb(${c.r}, ${c.g}, ${c.b})`);
    }
    cctx.fillStyle = gradient;
    cctx.fillRect(0, 0, W, H);
    // No canvas-level border or radius — the wrapping `.colorbar` div
    // owns the outline + rounded corners (style.css), and `overflow: hidden`
    // on that div clips the canvas to those corners. A second border here
    // would inset the gradient by one extra pixel and throw off tick math.
    canvas.style.margin = '0';
    // Replace any prior canvas but keep the mini-label child intact — it's
    // a sibling overlay sized via CSS, and re-creating it each repaint
    // would force a layout flush + lose its textContent set elsewhere.
    const oldCanvas = colorbar.querySelector('canvas');
    if (oldCanvas) oldCanvas.remove();
    colorbar.appendChild(canvas);
    updateColorbarLabels(ctx, ctx.scope ?? null);
}
